import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EquipmentTypeDto, HourlyLineItemDto, QuoteDto, RegionDto, SiteDto } from '../../../../../dtos';
import { emptyGuid } from '../../../../../util';
import { IFormProps, IFormFieldValidationConfig, isNotBlank, runFormValidation, runFieldValidation, isGreaterThanOrEqualTo } from '../../../../CoreLib/library';
import { SelectChangeEvent } from '@mui/material';
import { BilledToType } from '../../../../../dtos/enums/BilledToType';
import { useGetRegionQuery } from '../../../../../store/generated/generatedApi'

export interface IHourlyLineItemFormValues {
    quoteId: string;
    equipmentTypeId: string;
    siteId?: string;
    quantity?: number;
    driverRate: number;
    brokerRate: number;
    rate: number;
    hourlyType: string;
    lineItemMemo?: string;
    driverMemo?: string;
    isActive: boolean;
    customerJobNumber?: string;
    zone?: string;
    isBilledTo: BilledToType;
    billedTo?: string;
    poNumber?: string;
    haulToAddressLine1?: string;
    haulToAddressLine2?: string;
    haulToCountry?: string;
    haulToCity?: string;
    haulToState?: string;
    haulToZipCode?: string;
    haulToRegion?: RegionDto | undefined;
    isCustomPoNumber: boolean;
    isCustomerJobNumberCustom: boolean;
}

export const DEFAULT_HAULING_LINE_ITEM: HourlyLineItemDto = {
    id: emptyGuid,
    quoteId: '',
    equipmentTypeId: '',
    description: '',
    quantity: 1,
    driverRate: 0,
    brokerRate: 0,
    hourlyType: '',
    rate: 0,
    isActive: true,
    lineItemMemo: '',
    driverMemo: '',
    createdOn: new Date(),
    customerJobNumber: '',
    zone: '',
    isBilledTo: BilledToType.TriAx,
    billedTo: '',
    poNumber: '',
    haulToAddressLine1: '',
    haulToAddressLine2: '',
    haulToCountry: '',
    haulToCity: '',
    haulToState: '',
    haulToZipCode: '',
    haulToRegion: undefined,
    isCustomPoNumber: false,
    isCustomerJobNumberCustom: false,
};

export function useHourlyLineItemForm(props: IFormProps<HourlyLineItemDto>, quote: QuoteDto | undefined) {

    const { data: regions } = useGetRegionQuery({ searchText: '', sortKey: 'NAME', page: 0, pageSize: 100000, sortAsc: true, includeInactive: false });

    const { save, cancel, initValues } = props;
    const [formQuote, setFormQuote] = useState<QuoteDto>();
    const [formEquipmentType, setFormEquipmentType] = useState<EquipmentTypeDto>();
    const [formSite, setFormSite] = useState(initValues?.site);
    const [formRate, setFormRate] = useState<number>();
    const [formQuantity, setFormQuantity] = useState<number | undefined>(initValues?.quantity);
    const [formDriverRate, setFormDriverRate] = useState<number>();
    const [formBrokerRate, setFormBrokerRate] = useState<number>();
    const [formIsActive, setFormIsActive] = useState(true);
    const [formHourlyType, setFormHourlyType] = useState('');
    const [formLineItemMemo, setFormLineItemMemo] = useState<string | undefined>(initValues?.lineItemMemo);
    const [formDriverMemo, setFormDriverMemo] = useState<string | undefined>(initValues?.driverMemo);
    const [formCustomerJobNumber, setFormCustomerJobNumber] = useState<string | undefined>(
        initValues?.isCustomPoNumber ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? ''
    );
    const [formZone, setFormZone] = useState<string | undefined>(initValues?.zone ?? '');
    const [formIsBilledTo, setFormIsBilledTo] = useState<BilledToType>(initValues?.isBilledTo ?? BilledToType.TriAx);
    const [formBilledTo, setFormBilledTo] = useState<string | undefined>(initValues?.billedTo ?? '');
    const [formAddressLine1, setFormAddressLine1] = useState('');
    const [formAddressLine2, setFormAddressLine2] = useState('');
    const [formCountry, setFormCountry] = useState('');
    const [formCity, setFormCity] = useState('');
    const [formState, setFormState] = useState('');
    const [formZipCode, setFormZipCode] = useState('');
    const [formRegion, setFormRegion] = useState<RegionDto | undefined>();
    const [formPoNumber, setFormPoNumber] = useState<string | undefined>(
        initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? ''
    );
    const [formQuotePoNumber, setFormQuotePoNumber] = useState<string | undefined>(quote?.poNumber);
    const [formQuoteCustomerJobNumber, setFormQuoteCustomerJobNumber] = useState<string | undefined>(quote?.customerJobNumber);
    const [formIsCustomPoNumber, setFormIsCustomPoNumber] = useState<boolean>(initValues?.isCustomPoNumber ?? false);
    const [formIsCustomerJobNumberCustom, setFormIsCustomerJobNumberCustom] = useState<boolean>(initValues?.isCustomerJobNumberCustom ?? false);

    useEffect(() => {
        setFormQuote(initValues?.quote);
        setFormEquipmentType(initValues?.equipmentType);
        setFormSite(initValues?.site);
        setFormRate(initValues?.rate ?? 0);
        setFormQuantity(initValues?.quantity);
        setFormDriverRate(initValues?.driverRate ?? 0);
        setFormBrokerRate(initValues?.brokerRate ?? 0);
        setFormIsActive(initValues?.isActive ?? true);
        setFormHourlyType(initValues?.hourlyType ?? '');
        setFormLineItemMemo(initValues?.lineItemMemo);
        setFormDriverMemo(initValues?.driverMemo);
        setFormZone(initValues?.zone ?? '');
        setFormIsBilledTo(initValues?.isBilledTo ?? BilledToType.TriAx);
        setFormBilledTo(initValues?.billedTo);
        setFormAddressLine1(initValues?.haulToAddressLine1 ?? initValues?.site?.addressLine1 ?? '');
        setFormAddressLine2(initValues?.haulToAddressLine2 ?? initValues?.site?.addressLine2 ?? '');
        setFormCountry(initValues?.haulToCountry ?? initValues?.site?.country ?? '');
        setFormCity(initValues?.haulToCity ?? initValues?.site?.city ?? '');
        setFormState(initValues?.haulToState ?? initValues?.site?.state ?? '');
        setFormZipCode(initValues?.haulToZipCode ?? initValues?.site?.zipCode ?? '');
        setFormRegion(initValues?.haulToRegion ?? initValues?.site?.region ?? undefined);
        setFormQuotePoNumber(quote?.poNumber);
        setFormQuoteCustomerJobNumber(quote?.customerJobNumber);
        setFormPoNumber(initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? '');
        setFormCustomerJobNumber(initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? '');
    }, [initValues, quote?.poNumber, quote?.customerJobNumber]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IHourlyLineItemFormValues, string>>(
        new Map([
            ['quoteId', ''],
            ['equipmentTypeId', ''],
            ['siteId', ''],
            ['quantity', ''],
            ['rate', ''],
            ['driverRate', ''],
            ['brokerRate', ''],
            ['hourlyType', ''],
            ['lineItemMemo', ''],
            ['customerJobNumber', ''],
            ['isBilledTo', ''],
            ['billedTo', ''],
            ['poNumber', ''],
        ])
    );

    const formFieldValidators = useMemo(
        () =>
            new Map<keyof IHourlyLineItemFormValues, IFormFieldValidationConfig>([
                [
                    'quoteId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Quote',
                    },
                ],
                [
                    'equipmentTypeId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Equipment Type',
                    },
                ],
                [
                    'quantity',
                    {
                        validators: [isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Quantity',
                    },
                ],
                [
                    'rate',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Rate',
                    },
                ],
                [
                    'driverRate',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Driver Rate',
                    },
                ],
                [
                    'brokerRate',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Broker Rate',
                    },
                ],
                [
                    'hourlyType',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Type',
                    },
                ],
                [
                    'isBilledTo',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Is Billed To',
                    },
                ],
                [
                    'billedTo',
                    {
                        validators: formIsBilledTo === BilledToType.Custom ? [isNotBlank] : [],
                        errorMessageEntityName: 'Billed To',
                    },
                ],
                [
                    'poNumber',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'PO Number',
                    },
                ],
            ]),
        [formIsBilledTo]
    );

    const getCurrentFormValues = useCallback((): IHourlyLineItemFormValues => {
        return {
            quoteId: formQuote?.id ?? emptyGuid,
            equipmentTypeId: formEquipmentType?.id ?? emptyGuid,
            siteId: formSite?.id,
            quantity: formQuantity,
            rate: formRate ?? 0,
            driverRate: formDriverRate ?? 0,
            brokerRate: formBrokerRate ?? 0,
            hourlyType: formHourlyType,
            lineItemMemo: formLineItemMemo,
            driverMemo: formDriverMemo,
            isActive: formIsActive,
            customerJobNumber: formCustomerJobNumber,
            zone: formZone,
            isBilledTo: formIsBilledTo ?? BilledToType.TriAx,
            billedTo: formBilledTo,
            poNumber: formPoNumber,
            haulToAddressLine1: formAddressLine1,
            haulToAddressLine2: formAddressLine2,
            haulToCountry: formCountry,
            haulToCity: formCity,
            haulToState: formState,
            haulToZipCode: formZipCode,
            haulToRegion: formRegion,
            isCustomPoNumber: formIsCustomPoNumber,
            isCustomerJobNumberCustom: formIsCustomerJobNumberCustom,
        };
    }, [
        formBilledTo,
        formBrokerRate,
        formCustomerJobNumber,
        formDriverRate,
        formEquipmentType?.id,
        formSite?.id,
        formHourlyType,
        formIsActive,
        formIsBilledTo,
        formLineItemMemo,
        formDriverMemo,
        formPoNumber,
        formQuantity,
        formQuote?.id,
        formRate,
        formZone,
        formAddressLine1,
        formAddressLine2,
        formCountry,
        formCity,
        formState,
        formZipCode,
        formRegion,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IHourlyLineItemFormValues>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = (fieldName: keyof IHourlyLineItemFormValues) => {
        const validationConfig = formFieldValidators.get(fieldName);
        if (validationConfig) {
            const formValues = getCurrentFormValues();
            const fieldValue = formValues[fieldName];
            const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

            if (errorMessage !== fieldErrors.get(fieldName)) {
                const updatedFieldErrors = _.cloneDeep(fieldErrors);
                updatedFieldErrors.set(fieldName, errorMessage);
                setFieldErrors(updatedFieldErrors);
            }
        }
    };

    const isFormDirty = () => {
        var isDirty = formIsActive !== initValues?.isActive;
        isDirty = isDirty || formQuote?.id !== initValues?.quote?.id;
        isDirty = isDirty || formEquipmentType !== initValues?.equipmentType;
        isDirty = isDirty || formSite?.id !== initValues?.site?.id;
        isDirty = isDirty || formQuantity !== initValues?.quantity;
        isDirty = isDirty || formRate !== initValues?.rate;
        isDirty = isDirty || formDriverRate !== initValues?.driverRate;
        isDirty = isDirty || formBrokerRate !== initValues?.brokerRate;
        isDirty = isDirty || formHourlyType !== initValues?.hourlyType;
        isDirty = isDirty || formLineItemMemo !== initValues?.lineItemMemo;
        isDirty = isDirty || formDriverMemo !== initValues?.driverMemo;
        isDirty = isDirty || formCustomerJobNumber !== initValues?.customerJobNumber;
        isDirty = isDirty || formZone !== initValues?.zone;
        isDirty = isDirty || formIsBilledTo !== initValues?.isBilledTo;
        isDirty = isDirty || formBilledTo !== initValues?.billedTo;
        isDirty = isDirty || formPoNumber !== initValues?.poNumber;
        isDirty = isDirty || formAddressLine1 !== initValues?.haulToAddressLine1;
        isDirty = isDirty || formAddressLine2 !== initValues?.haulToAddressLine2;
        isDirty = isDirty || formCountry !== initValues?.haulToCountry;
        isDirty = isDirty || formCity !== initValues?.haulToCity;
        isDirty = isDirty || formState !== initValues?.haulToState;
        isDirty = isDirty || formZipCode !== initValues?.haulToZipCode;
        isDirty = isDirty || formRegion !== initValues?.haulToRegion;
        isDirty = isDirty || formIsCustomPoNumber !== initValues?.isCustomPoNumber;
        isDirty = isDirty || formIsCustomerJobNumberCustom !== initValues?.isCustomerJobNumberCustom;

        return isDirty;
    };

    const handleQuoteChange = useCallback((quote: QuoteDto) => {
        setFormQuote(quote);
    }, []);

    const handleEquipmentTypeChange = useCallback((equipmentType?: EquipmentTypeDto) => {
        setFormEquipmentType(equipmentType);
    }, []);

    const handleFormAddressChange = useCallback((address) => {
        setFormAddressLine1(address.addressLine1);
        setFormAddressLine2(address.addressLine2);
        setFormCountry(address.country);
        setFormCity(address.city);
        setFormState(address.state);
        setFormZipCode(address.zipCode);
    }, []);

    const handleFormRegionChange = useCallback((region: RegionDto | undefined) => {
        setFormRegion(region);
    }, []);

    const handleSiteChange = useCallback((site?: SiteDto) => {
        setFormSite(site);
        handleFormAddressChange({
            addressLine1: site?.addressLine1,
            addressLine2: site?.addressLine2,
            country: site?.country,
            city: site?.city,
            state: site?.state,
            zipCode: site?.zipCode
        });
        handleFormRegionChange(site?.region);
    }, [handleFormAddressChange, handleFormRegionChange]);

    const handleQuantityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormQuantity(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleDriverRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDriverRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleBrokerRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormBrokerRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
    }, []);

    const handleHourlyTypeChange = useCallback((event: SelectChangeEvent<string>) => {
        setFormHourlyType(event.target.value);
    }, []);

    const handleLineItemMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormLineItemMemo(event.target.value ?? '');
    }, []);

    const handleDriverMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDriverMemo(event.target.value ?? '');
    }, []);

    const handleCustomerJobNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerJobNumber(event.target.value ?? '');
    }, []);

    const handleZoneChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormZone(event.target.value ?? '');
    }, []);

    const handleIsBilledToChange = useCallback((isBilledTo?: BilledToType) => {
        setFormIsBilledTo(isBilledTo ?? BilledToType.TriAx);
    }, []);

    const handleBilledToChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormBilledTo(event.target.value ?? '');
    }, []);

    const handlePoNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormPoNumber(event.target.value ?? '');
    }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_HAULING_LINE_ITEM),
                ...formValues,
            };
            save(updatedUserRecord);
        }
    }, [getCurrentFormValues, initValues, save, validateForm]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handleIsCustomPoNumberChange = useCallback((value: boolean) => {
        setFormIsCustomPoNumber(value);
        if (!value) {
            setFormPoNumber(formQuotePoNumber);
        }
    }, [formQuotePoNumber, setFormPoNumber]);

    const handleIsCustomerJobNumberCustomChange = useCallback((value: boolean) => {
        setFormIsCustomerJobNumberCustom(value);
        if (!value) {
            setFormCustomerJobNumber(formQuoteCustomerJobNumber);
        }
    }, [formQuoteCustomerJobNumber, setFormCustomerJobNumber]);

    const formTotal = useMemo(() => {
        return (formRate ?? 0) * (formQuantity ?? 0);
    }, [formRate, formQuantity]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleSiteChange,
        handleQuantityChange,
        handleRateChange,
        handleDriverRateChange,
        handleBrokerRateChange,
        handleIsActiveChange,
        handleHourlyTypeChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleCustomerJobNumberChange,
        handleZoneChange,
        handleIsBilledToChange,
        handleBilledToChange,
        handlePoNumberChange,
        handleFormAddressChange,
        handleFormRegionChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        formQuote,
        formEquipmentType,
        formSite,
        formQuantity,
        formRate,
        formDriverRate,
        formBrokerRate,
        formIsActive,
        formHourlyType,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formZone,
        formIsBilledTo,
        formBilledTo,
        formPoNumber,
        formAddressLine1,
        formAddressLine2,
        formCountry,
        formCity,
        formState,
        formZipCode,
        regions,
        formRegion,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        validateField,
        initValues,
        formTotal,
    };
}
